import { Emitter, onStorageChange, storageGet, storageSet } from "./storage.js";
import { loadSnapshot, type PriceAlert, type TickerSnapshot } from "./market-data.js";
import { syncArmedAlerts } from "./web-push.js";

const TICKERS_KEY = "watchlistTickers";
const ALERTS_KEY = "watchlistAlerts";
const SNAPSHOTS_KEY = "watchlistSnapshots";
const UPDATED_KEY = "watchlistLastUpdated";
const REFRESH_INTERVAL_MS = 60 * 1000; // auto-refresh prices every minute (D-094) — 1m is the finest Scan timeframe

/**
 * Ported from the native app's WatchlistStore. UserDefaults was
 * synchronous; chrome.storage.local is not, so every mutating method here
 * is async. Snapshots are persisted too, so a fresh popup opens showing
 * the last-known data immediately while a new refresh runs. A ticker is
 * fetched the moment it's added, and the whole list auto-refreshes on open
 * and every minute while the popup stays open — no manual refresh button
 * (D-094). A failed fetch keeps the prior value rather than blanking it.
 */
export class WatchlistStore {
  tickers: string[] = [];
  snapshots: Record<string, TickerSnapshot> = {};
  alerts: Record<string, PriceAlert> = {};
  lastUpdated: Date | null = null;
  isRefreshing = false;

  readonly changed = new Emitter();
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  async init(): Promise<void> {
    this.tickers = await storageGet<string[]>(TICKERS_KEY, []);
    this.alerts = await storageGet<Record<string, PriceAlert>>(ALERTS_KEY, {});
    this.snapshots = await storageGet<Record<string, TickerSnapshot>>(SNAPSHOTS_KEY, {});
    const updated = await storageGet<number | null>(UPDATED_KEY, null);
    this.lastUpdated = updated != null ? new Date(updated) : null;

    // Another context (the background worker's 5-minute refresh, or a
    // second window) may update these — mirror the changes live.
    onStorageChange<Record<string, TickerSnapshot>>(SNAPSHOTS_KEY, (v) => {
      this.snapshots = v ?? {};
      this.changed.emit();
    });
    onStorageChange<Record<string, PriceAlert>>(ALERTS_KEY, (v) => {
      this.alerts = v ?? {};
      this.changed.emit();
    });
    onStorageChange<string[]>(TICKERS_KEY, (v) => {
      this.tickers = v ?? [];
      this.changed.emit();
    });

    this.changed.emit();
    this.scheduleTimer();
    void this.refresh();
  }

  private async persistTickers(): Promise<void> {
    await storageSet(TICKERS_KEY, this.tickers);
  }

  private async persistAlerts(): Promise<void> {
    await storageSet(ALERTS_KEY, this.alerts);
    // Mirror the armed set to the server for the D-137 scan (web + opted-in only;
    // no-op otherwise). Fire-and-forget — never block a local mutation on it.
    void syncArmedAlerts(Object.values(this.alerts));
  }

  private async persistSnapshots(): Promise<void> {
    await storageSet(SNAPSHOTS_KEY, this.snapshots);
  }

  async addTicker(raw: string): Promise<void> {
    const symbol = raw.trim().toUpperCase();
    if (!symbol || this.tickers.includes(symbol)) return;
    this.tickers.push(symbol);
    await this.persistTickers();
    this.changed.emit();
    // Poll for the new ticker's data immediately.
    await this.refreshOne(symbol);
  }

  async removeTicker(symbol: string): Promise<void> {
    this.tickers = this.tickers.filter((t) => t !== symbol);
    delete this.snapshots[symbol];
    delete this.alerts[symbol];
    await this.persistTickers();
    await this.persistAlerts();
    await this.persistSnapshots();
    this.changed.emit();
  }

  async setAlert(symbol: string, targetPrice: number): Promise<void> {
    const currentPrice = this.snapshots[symbol]?.currentPrice;
    if (currentPrice == null) return;
    this.alerts[symbol] = { symbol, targetPrice, wasAbove: currentPrice >= targetPrice };
    await this.persistAlerts();
    this.changed.emit();
  }

  async removeAlert(symbol: string): Promise<void> {
    delete this.alerts[symbol];
    await this.persistAlerts();
    this.changed.emit();
  }

  private scheduleTimer(): void {
    if (this.refreshTimer != null) clearInterval(this.refreshTimer);
    this.refreshTimer = setInterval(() => void this.refresh(), REFRESH_INTERVAL_MS);
  }

  async refresh(): Promise<void> {
    if (this.isRefreshing) return;
    this.isRefreshing = true;
    this.changed.emit();
    try {
      for (const symbol of this.tickers) {
        await this.refreshOne(symbol);
        await new Promise((r) => setTimeout(r, 400));
      }
      this.lastUpdated = new Date();
      await storageSet(UPDATED_KEY, this.lastUpdated.getTime());
    } finally {
      // Always clear the flag even if something threw — otherwise a single failure
      // would lock out every future refresh.
      this.isRefreshing = false;
      this.changed.emit();
    }
  }

  private async refreshOne(symbol: string): Promise<void> {
    // Keep the prior value on failure (D-094): a stale-but-real number beats a
    // gap. A fetch error just leaves the last snapshot in place and moves on.
    try {
      this.snapshots[symbol] = await loadSnapshot(symbol);
      await this.persistSnapshots();
    } catch {
      /* keep the previous snapshot for this symbol */
    }
    this.changed.emit();
  }
}
